'use client';

import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { Resolver } from 'react-hook-form';
import * as Yup from 'yup';
import {
	Modal,
	ModalBody,
	Form,
	FormGroup,
	Label,
	Button,
	ModalFooter,
} from 'reactstrap';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import CustomInputField from 'src/components/shared/form-fields/custom-input-field';
import ButtonWithLoader from 'src/components/shared/form-fields/loading-button';
import { deleteResidentResponsibility, fetchJobActivityLog } from 'src/redux/jobs/api';
import { handleErrorMessages } from 'src/helpers/utils';
import type { ThunkRejectValue } from 'src/types/api';
import { toast } from 'react-toastify';

interface RemoveResidentResponsibilityFormData {
	reason_for_change: string;
}

export interface ResidentResponsibilityRemoveModalProps {
  rootNodeId: number;
  jobId: number;
}

const MAX_CHARS = 500;

const validationSchema = Yup.object().shape({
	reason_for_change: Yup.string()
		.trim()
		.required('This is a required field')
		.max(MAX_CHARS, `Maximum ${MAX_CHARS} characters allowed`),
});

const ResidentResponsibilityRemoveModal = () => {
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useAppDispatch();

	const srId = useAppSelector((state) => state?.jobDetails?.slideOut?.srId);
	const slideOutType = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const { extraProps } = useAppSelector((state: Record<string, unknown>) => {
		const modals = state?.modals as Record<string, unknown>;
		return modals?.residentResponsibilityRemoveModal as Record<string, unknown>;
	});
  const props = extraProps as ResidentResponsibilityRemoveModalProps;
  const rootNodeId = props?.rootNodeId;
  const jobId = props?.jobId;


	const methods = useForm<RemoveResidentResponsibilityFormData>({
		mode: 'all',
		defaultValues: { reason_for_change: '' },
		resolver: yupResolver(
			validationSchema,
		) as unknown as Resolver<RemoveResidentResponsibilityFormData>,
	});

	const { handleSubmit, formState: { isValid } } = methods;

	const handleClose = useCallback(() => {
		dispatch(
			updateVisibility({
				modalType: 'residentResponsibilityRemoveModal',
				isVisible: false,
			}),
		);
	}, [dispatch]);

	const onSubmit = (formData: RemoveResidentResponsibilityFormData) => {
		setIsLoading(true);
		dispatch(deleteResidentResponsibility(jobId)(formData))
			.unwrap()
			.then((res) => {
        toast((res as { message?: string })?.message, { type: 'success' });
				dispatch(fetchJobActivityLog(srId)({
					...(slideOutType === 'jobDetails' ? { job_id: rootNodeId } : {}),
				}));
				window.dispatchEvent(new CustomEvent('resident-responsibility-updated', {
					detail: {
						jobId: rootNodeId,
						is_resident_caused_problem: '',
						resident_problem_description: [],
					},
				}));
				handleClose();
			})
			.catch((error: ThunkRejectValue) => {
				handleErrorMessages(
					error?.data?.errors ?? error?.errors ?? error?.message ?? null,
					(msg: string) => toast(msg, { type: 'error' }),
				);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<Modal
			isOpen
			toggle={handleClose}
			centered
			modalClassName="common-confirmation-modal"
		>
			<div className="modal-header">
				<h5 className="modal-title">Resident Responsibility</h5>
				<button
					type="button"
					className="btn-close"
					aria-label="Close"
					onClick={handleClose}
				/>
			</div>
			<FormProvider {...methods}>
				<Form onSubmit={handleSubmit(onSubmit)}>
					<ModalBody>
						<div className="body-sm mb-4">
							This clears the flag, updates the Work Order, and syncs to your
							PMS.
						</div>
						<FormGroup className="form-group no-margin">
							<Label for="reason_for_change">
								Reason for Removal
								<span className="text-danger"> *</span>
							</Label>
							<CustomInputField
								name="reason_for_change"
								type="textarea"
								id="reason_for_change"
								placeholder="Describe reason for removal..."
							/>
						</FormGroup>
					</ModalBody>
					<ModalFooter>
						<Button
							color="none"
							className="secondary-btn"
							type="button"
							onClick={handleClose}
							>
							Cancel
						</Button>
						<ButtonWithLoader
							color="none"
							className="primary-btn"
							type="submit"
							isLoading={isLoading}
							disabled={!isValid}
						>
							Remove Flag
						</ButtonWithLoader>
					</ModalFooter>
				</Form>
			</FormProvider>
		</Modal>
	);
};

export default ResidentResponsibilityRemoveModal;
